import cdnLoader from '../../cdn-loader';

export const PRODUCT_MAIN_IMAGE_WIDTHS = [390, 430, 640, 768, 1024] as const;
export const PRODUCT_THUMBNAIL_WIDTHS = [100, 200, 300] as const;
export const SEO_IMAGE_WIDTH = 1024;
export const SEO_IMAGE_QUALITY = 85;

const DEFAULT_QUALITY = 85;

export function toSeoCdnImageUrl(src?: string | null): string | undefined {
  if (!src) return undefined;
  return cdnLoader({ src, width: SEO_IMAGE_WIDTH, quality: SEO_IMAGE_QUALITY });
}

function normalizeProductImageName(str: string): string {
  return str
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replaceAll('&', '')
    .replaceAll(',', '')
    .replaceAll('/', '-')
    .replaceAll('  ', ' ')
    .replaceAll(' ', '-')
    .replaceAll('ı', 'i')
    .replaceAll("'", '-')
    .replaceAll('’', '-')
    .replaceAll('(', '-')
    .replaceAll(')', '-')
    .replaceAll(':', '-')
    .replace(/-+/g, '-')
    .replace(/-$/g, '')
    .replace(/^-/g, '')
    .toLowerCase();
}

export function toProductImageSrc({
  src,
  title,
  id,
  type = 'product',
}: {
  src?: string | null;
  title?: string | null;
  id?: number | string | null;
  type?: 'product' | 'image';
}): string | undefined {
  if (!src || src === 'null' || src === 'undefined') return undefined;
  if (!title || id === undefined || id === null || id === '') return src;

  try {
    const url = new URL(src);
    const pathParts = url.pathname.split('/');
    const filename = pathParts[pathParts.length - 1];
    const pathToFilename = pathParts.slice(0, -1).join('/');
    const fileExtension = filename.split('.').pop();
    const idPointer = type === 'product' ? `p${id}` : `i${id}`;
    return `${url.protocol}//${url.hostname}${pathToFilename}/${normalizeProductImageName(title)}-${idPointer}.${fileExtension}`;
  } catch {
    return src;
  }
}

export function toSeoCdnProductImageUrl({
  src,
  title,
  id,
  type = 'product',
}: {
  src?: string | null;
  title?: string | null;
  id?: number | string | null;
  type?: 'product' | 'image';
}): string | undefined {
  return toSeoCdnImageUrl(toProductImageSrc({ src, title, id, type }));
}

export type SrcSetCandidate = {
  url: string;
  width: number;
};

export function uniqueSrcSetCandidates(candidates: SrcSetCandidate[]): SrcSetCandidate[] {
  const seen = new Set<string>();
  return candidates.filter(({ url, width }) => {
    const key = `${url} ${width}w`;
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
}

export function formatSrcSet(candidates: SrcSetCandidate[]): string {
  return uniqueSrcSetCandidates(candidates)
    .map(({ url, width }) => `${url} ${width}w`)
    .join(', ');
}

export function buildCdnSrcSet({
  src,
  widths,
  quality = DEFAULT_QUALITY,
  fallbackWidth,
}: {
  src: string;
  widths: readonly number[];
  quality?: number;
  fallbackWidth?: number;
}): { src: string; srcSet: string } {
  const uniqueWidths = [...new Set(widths)].filter((width) => width > 0).sort((a, b) => a - b);

  const candidates = uniqueSrcSetCandidates(
    uniqueWidths.map((width) => ({
      url: cdnLoader({ src, width, quality }),
      width,
    })),
  );

  const resolvedFallbackWidth =
    fallbackWidth && uniqueWidths.includes(fallbackWidth) ? fallbackWidth : uniqueWidths[0];

  return {
    src: cdnLoader({ src, width: resolvedFallbackWidth, quality }),
    srcSet: formatSrcSet(candidates),
  };
}
